/**
 * Layout Component
 *
 * Main application layout with a channel-centric sidebar.
 *
 * Sidebar structure:
 * - Dashboard (overview + add channel)
 * - Channels: a switcher listing the user's channels. Each channel expands
 *   to nested items (Overview, Commands, Modules, Overlays) scoped to that channel.
 * - Groups: shared-module groups (stub — not built yet).
 * - Admin (admin only): Module registry, Chat commands, Bot accounts.
 * - Footer: connection status, account Settings, Bot override, Log out.
 *
 * Account settings live in the footer (account-scoped, not per-channel).
 * Channels are fetched once on mount. The active channel auto-expands based
 * on the current route. Provides the WebSocket connection via WebSocketProvider.
 */

import { useState, useEffect, useCallback } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useAuth, getStoredAuthMethod } from '@/hooks/useAuth'
import { useWebSocket, WebSocketProvider } from '@/hooks/useWebSocket'
import api from '@/api/client'
import { DatabaseBanner } from '@/components/DatabaseBanner'
import {
  LayoutDashboard,
  Radio,
  Settings,
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff,
  Puzzle,
  Layers,
  Plus,
  ChevronRight,
  ChevronDown,
  UsersRound,
  SlidersHorizontal,
  Terminal,
  Bot,
  ShieldAlert,
} from 'lucide-react'
import clsx from 'clsx'

/**
 * Minimal channel shape needed for the nav switcher.
 */
interface NavChannel {
  id: number
  twitch_channel_name: string
  bot_connected: boolean
  bot_enabled: boolean
}

/**
 * Section heading (uppercase label above a nav group).
 */
function NavHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
      {children}
    </div>
  )
}

/**
 * Top-level nav link (Dashboard, Groups, Admin items).
 */
function NavItem({
  to,
  icon: Icon,
  label,
  onClick,
  end = false,
  trailing,
}: {
  to: string
  icon: React.ElementType
  label: string
  onClick?: () => void
  end?: boolean
  trailing?: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      end={end}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors',
          isActive
            ? 'bg-synth-magenta/15 text-synth-magenta border-l-2 border-synth-magenta pl-[10px]'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-overlay'
        )
      }
    >
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {trailing}
    </NavLink>
  )
}

/**
 * Status dot reflecting bot connection state.
 */
function StatusDot({ channel }: { channel: NavChannel }) {
  const color = !channel.bot_enabled
    ? 'bg-neutral-600'
    : channel.bot_connected
      ? 'bg-synth-mint'
      : 'bg-state-warning'
  return <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', color)} />
}

/**
 * A single channel in the switcher: a header row that toggles expansion,
 * plus nested per-channel links when expanded.
 */
function ChannelGroup({
  channel,
  expanded,
  onToggle,
  onNavigate,
}: {
  channel: NavChannel
  expanded: boolean
  onToggle: () => void
  onNavigate: () => void
}) {
  const base = `/channels/${channel.id}`
  const subLink = (
    to: string,
    Icon: React.ElementType,
    label: string,
    end = false,
  ) => (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-2.5 px-2.5 py-1.5 rounded text-[13px] transition-colors',
          isActive
            ? 'bg-synth-magenta/15 text-synth-magenta'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-overlay'
        )
      }
    >
      <Icon size={15} />
      <span>{label}</span>
    </NavLink>
  )

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-text-primary hover:bg-surface-overlay transition-colors"
      >
        {expanded ? (
          <ChevronDown size={15} className="text-text-muted shrink-0" />
        ) : (
          <ChevronRight size={15} className="text-text-muted shrink-0" />
        )}
        <StatusDot channel={channel} />
        <span className="flex-1 text-left truncate">
          {channel.twitch_channel_name}
        </span>
      </button>

      {expanded && (
        <div className="ml-[18px] pl-2.5 my-0.5 border-l border-border space-y-0.5">
          {subLink(`${base}`, Radio, 'Overview', true)}
          {subLink(`${base}/commands`, Terminal, 'Commands')}
          {subLink(`${base}/modules`, Puzzle, 'Modules')}
          {subLink(`${base}/overlays`, Layers, 'Overlays')}
        </div>
      )}
    </div>
  )
}

/**
 * Sidebar navigation.
 */
function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user, logout } = useAuth()
  const { isConnected } = useWebSocket()
  const location = useLocation()

  const [channels, setChannels] = useState<NavChannel[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // Fetch channels once on mount.
  useEffect(() => {
    let mounted = true
    api
      .get<NavChannel[]>('/api/channels')
      .then((data) => {
        if (mounted) setChannels(data)
      })
      .catch(() => {
        // Non-fatal: nav still renders without the channel list.
      })
    return () => {
      mounted = false
    }
  }, [])

  // Auto-expand the channel that matches the current route.
  useEffect(() => {
    const match = location.pathname.match(/^\/channels\/(\d+)/)
    if (match) {
      const id = parseInt(match[1], 10)
      setExpanded((prev) => {
        if (prev.has(id)) return prev
        const next = new Set(prev)
        next.add(id)
        return next
      })
    }
  }, [location.pathname])

  const toggle = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-64 bg-surface-base border-r border-border',
          'transform transition-transform duration-200 ease-in-out',
          'lg:translate-x-0 flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-border shrink-0">
          <span className="text-xl font-bold text-gradient">PratBot</span>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-text-muted hover:text-text-primary"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto px-2 py-3">
          <NavItem
            to="/"
            icon={LayoutDashboard}
            label="Dashboard"
            onClick={onClose}
            end
          />

          <NavHeading>Channels</NavHeading>
          <div className="space-y-0.5">
            {channels.map((ch) => (
              <ChannelGroup
                key={ch.id}
                channel={ch}
                expanded={expanded.has(ch.id)}
                onToggle={() => toggle(ch.id)}
                onNavigate={onClose}
              />
            ))}
            <NavLink
              to="/channels"
              onClick={onClose}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] text-synth-cyan hover:bg-surface-overlay transition-colors"
            >
              <Plus size={15} />
              <span>Add channel</span>
            </NavLink>
          </div>

          <NavHeading>Groups</NavHeading>
          <NavItem
            to="/groups"
            icon={UsersRound}
            label="Groups"
            onClick={onClose}
            trailing={
              <span className="text-[10px] px-1.5 py-px rounded-sm bg-synth-cyan/15 text-synth-cyan">
                soon
              </span>
            }
          />

          {user?.isAdmin && (
            <>
              <NavHeading>Admin</NavHeading>
              <NavItem
                to="/admin/modules"
                icon={SlidersHorizontal}
                label="Module registry"
                onClick={onClose}
              />
              <NavItem
                to="/admin/commands"
                icon={Terminal}
                label="Chat commands"
                onClick={onClose}
              />
              <NavItem
                to="/admin/bots"
                icon={Bot}
                label="Bot accounts"
                onClick={onClose}
              />
            </>
          )}
        </div>

        {/* User footer */}
        <div className="shrink-0 p-3 border-t border-border space-y-1">
          {/* Connection status */}
          <div
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 mb-1 rounded text-xs',
              isConnected
                ? 'bg-synth-mint/15 text-synth-mint'
                : 'bg-state-danger-bg text-state-danger'
            )}
          >
            {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>

          {/* Account identity */}
          <div className="flex items-center gap-3 px-1 py-2">
            <div className="w-9 h-9 rounded-full bg-synth-magenta/20 flex items-center justify-center shrink-0">
              <span className="text-base font-medium text-synth-magenta">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {user?.username || 'Unknown'}
              </p>
              <p className="text-xs text-text-muted">
                {user?.isAdmin ? 'Admin' : 'User'}
              </p>
            </div>
          </div>

          {/* Account settings.
              `end` matters now that /settings/bot exists as a sibling route —
              without it this link stays highlighted while the child is open. */}
          <NavLink
            to="/settings"
            onClick={onClose}
            end
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-2 w-full px-3 py-2 rounded text-sm transition-colors',
                isActive
                  ? 'bg-synth-magenta/15 text-synth-magenta'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-overlay'
              )
            }
          >
            <Settings size={18} />
            <span>Settings</span>
          </NavLink>

          {/* Bot override — account-scoped, not per-channel: one bot replaces
              the system bot across every channel this user owns. */}
          <NavLink
            to="/settings/bot"
            onClick={onClose}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-2 w-full px-3 py-2 rounded text-sm transition-colors',
                isActive
                  ? 'bg-synth-magenta/15 text-synth-magenta'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-overlay'
              )
            }
          >
            <Bot size={18} />
            <span>Bot override</span>
          </NavLink>

          <button
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 text-text-secondary hover:text-text-primary hover:bg-surface-overlay rounded transition-colors"
          >
            <LogOut size={18} />
            <span>Log out</span>
          </button>
        </div>
      </aside>
    </>
  )
}

/**
 * Emergency-session banner.
 *
 * A break-glass session is super-admin, short-lived and obtained without
 * Twitch, so it must never be ambiguous that you are on one — an operator who
 * forgets is an operator who leaves an emergency session open. Reads the JWT's
 * `auth_method` claim through `getStoredAuthMethod()`; see that function for
 * why the claim is not carried as a field on `User`.
 */
function BreakGlassBanner() {
  if (getStoredAuthMethod() !== 'break_glass') return null

  return (
    <div
      role="status"
      className="flex items-start gap-2.5 px-4 lg:px-6 py-2.5 bg-state-warning-bg border-b border-state-warning/40 text-sm text-text-primary"
    >
      <ShieldAlert size={16} className="text-state-warning shrink-0 mt-0.5" />
      <span>
        <strong className="font-medium">Emergency access session.</strong>{' '}
        Signed in with break-glass credentials rather than Twitch. This session
        expires 60 minutes after sign-in.
      </span>
    </div>
  )
}

/**
 * LayoutContent - inner layout that uses the WebSocket context.
 *
 * No top bar: connection status moved into the sidebar footer. A floating
 * menu button (mobile only) opens the sidebar when it's collapsed.
 */
function LayoutContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="min-h-screen bg-surface-app">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Floating menu button (mobile only) */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
          className="lg:hidden fixed top-3 left-3 z-30 p-2 rounded bg-surface-base border border-border text-text-secondary hover:text-text-primary"
        >
          <Menu size={22} />
        </button>
      )}

      <div className="lg:ml-64">
        <BreakGlassBanner />
        <DatabaseBanner />
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * Layout - main application layout. Provides WebSocket context.
 *
 * ToastProvider used to live here. It was hoisted to `main.tsx` in R16b so that
 * unauthenticated pages OUTSIDE this shell — `/admin/break-glass` — can report
 * failures too; `useToast()` throws outside a provider, so leaving it here made
 * the emergency page the one page unable to say what went wrong. Everything
 * beneath the <Outlet/> still reaches it, from further up the tree.
 */
export default function Layout() {
  return (
    <WebSocketProvider>
      <LayoutContent />
    </WebSocketProvider>
  )
}
